/**
 * Стандартный блок ошибки в приложении.
 * Для ошибок сети — пульсирующее сердце (как в OfflineScreen).
 * Для остальных — иконка предупреждения.
 */

import { IconAlertTriangle } from "./Icons.tsx";
import { getErrorMessage, isNetworkError } from "../lib/graphql.ts";

/** Пульсирующее сердце (как в PageLoader / OfflineScreen) */
function PulsingHeart() {
  return (
    <div className="animate-pulse">
      <svg viewBox="0 0 48 48" className="w-16 h-16 xs:w-20 xs:h-20">
        <path
          d="M24 42c-1.5-1.5-4-3.5-7-6.5C11 30 4 23 4 15c0-6 5-11 11-11 3.5 0 6.5 1.5 9 4.5 2.5-3 5.5-4.5 9-4.5 6 0 11 5 11 11 0 8-7 15-13 20.5-3 3-5.5 5-7 6.5z"
          fill="#fbbf24"
        />
        <path
          d="M14 14c1.5-2 4-3 6-2"
          stroke="#fef3c7"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          opacity="0.7"
        />
      </svg>
    </div>
  );
}

export interface ErrorDisplayProps {
  error: unknown;
  onRetry: () => void;
  /** Заголовок (по умолчанию: "Ошибка сети" / "Ошибка загрузки") */
  title?: string;
  /** Текст кнопки (по умолчанию: "Попробовать снова") */
  buttonText?: string;
  /** Дополнительные классы для контейнера */
  className?: string;
}

export function ErrorDisplay({
  error,
  onRetry,
  title,
  buttonText = "Попробовать снова",
  className = "",
}: ErrorDisplayProps) {
  const isNetwork = isNetworkError(error);
  const message = getErrorMessage(error);
  const displayTitle =
    title ?? (isNetwork ? "Ошибка сети" : "Ошибка загрузки");

  return (
    <div
      className={`rounded-2xl overflow-hidden bg-white/95 shadow-sm p-6 xs:p-8 text-center ${className}`}
    >
      {isNetwork ? (
        <div className="mb-4 flex justify-center">
          <PulsingHeart />
        </div>
      ) : (
        <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 text-amber-500" />
      )}
      <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
        {displayTitle}
      </h2>
      <p className="text-slate-600 mb-4 text-sm">{message}</p>
      <button
        onClick={onRetry}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98]"
        style={{
          background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
        }}
      >
        {buttonText}
      </button>
    </div>
  );
}
